import "./FlowCanvas.css";
import React, { useRef, useState } from "react";
import { Button, Tooltip } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { BsFillPlayCircleFill } from "react-icons/bs";
import { getNameFromAction, getNameFromTriggerByType } from "./Actions/utils/functions";
import { buildFlowTree, findUnreachableStepsFromTree, laneClosesFlow, laneIsEnded, switchContinuesOnNoMatch } from "./flowTree";

/* Geometria fixa que permite calcular o layout vertical em JS:
 * o card tem 62px e a "linha do fluxo" de um nó passa no centro dele (y=31);
 * a caixa do nó tem altura fixa (card + nome + legenda clampados). Com isso,
 * a altura de cada faixa e o centro de cada bloco de desvio são calculáveis,
 * e os trilhos do garfo/merge fecham exatos — em Y — sem fios soltos. */
const NODE_H = 124;
const LINE = 31;
const LANE_GAP = 28;
const EMPTY_LANE_H = 62;

/* Largura das canaletas de garfo/merge. Precisa de folga horizontal para a
 * curva respirar: com menos de ~40px a Bézier vira quase um canto reto. */
const GUTTER_W = 56;

/* Faixa extra, abaixo da pilha, por onde passa o contorno do fallback
 * ("Continuar no fluxo principal"). Ele precisa de uma linha só sua: desenhado
 * na altura do desvio — o centro do bloco — ele encostava nas faixas do meio e
 * lia como saída de um passo da bifurcação, que é o oposto do que representa. */
const FALLBACK_RAIL_GAP = 30;

/** Aresta suave entre dois pontos, no formato das ferramentas de fluxo
 * (n8n, Make, React Flow): cúbica com tangentes horizontais nas duas pontas,
 * de modo que a linha entra e sai sempre na horizontal — nunca em diagonal.
 * Quando os dois pontos estão na mesma altura, degenera para um traço reto. */
const edgePath = (x0, y0, x1, y1) => {
    if (Math.abs(y1 - y0) < 0.5) return `M ${x0} ${y0} L ${x1} ${y1}`;
    const bend = (x1 - x0) * 0.62;
    return `M ${x0} ${y0} C ${x0 + bend} ${y0}, ${x1 - bend} ${y1}, ${x1} ${y1}`;
};

/** Mede uma faixa: um grupo de junção, ou a sequência de passos dela. */
const measureLane = (branch) => {
    if (branch.kind === "group") return measureGroup(branch);
    return branch.children.length
        ? measureLevel(branch.children)
        : { lineY: LINE, height: EMPTY_LANE_H };
};

/**
 * Empilha faixas: a altura total e a linha de fluxo de cada uma dentro da
 * pilha. A linha da pilha fica no meio entre a primeira e a última faixa — é
 * o que centraliza o card do desvio (e o da junção) entre os caminhos.
 */
const measureStack = (branches) => {
    const lanes = branches.map(measureLane);
    const laneLines = [];
    let offset = 0;
    lanes.forEach((lane, index) => {
        laneLines.push(offset + lane.lineY);
        offset += lane.height + (index < lanes.length - 1 ? LANE_GAP : 0);
    });
    const firstLine = laneLines[0] ?? LINE;
    const lastLine = laneLines[laneLines.length - 1] ?? LINE;
    return {
        lanes,
        laneLines,
        stackHeight: offset,
        lineY: Math.max(LINE, Math.round((firstLine + lastLine) / 2)),
    };
};

/**
 * Grupo de junção: a pilha das faixas unidas à esquerda e, na mesma linha, o
 * card da junção com a continuação comum à direita. Mede como um nível de dois
 * elementos — a linha do grupo é a maior das duas, e a altura, a de quem
 * precisar de mais espaço abaixo dela.
 */
const measureGroup = (group) => {
    const stack = measureStack(group.lanes);
    const tail = measureLevel(group.children);
    // A continuação divide a linha com o card da junção, que mede NODE_H a
    // partir dela — como qualquer nó simples de um nível.
    const tailHeight = Math.max(tail.lineY - LINE + NODE_H, tail.height);
    const lineY = Math.max(stack.lineY, tail.lineY);
    const stackOffset = lineY - stack.lineY;
    return {
        lineY,
        height: Math.max(
            stackOffset + stack.stackHeight,
            lineY - tail.lineY + tailHeight
        ),
        stack,
        tail,
        stackOffset,
        // Linhas das faixas unidas já na coordenada da caixa do grupo: é delas
        // que saem as curvas que entram no card da junção.
        laneLines: stack.laneLines.map((y) => y + stackOffset),
    };
};

/**
 * Linhas onde o garfo do desvio precisa entregar o fluxo: uma por caminho
 * declarado, não uma por faixa desenhada. Um grupo de junção ocupa uma faixa
 * só, mas continua sendo vários caminhos — sem descer até eles, os caminhos
 * unidos ficariam sem fio de entrada e sobraria uma curva apontando para o vão
 * entre as faixas.
 */
const collectEntryLines = (branch, laneMeasure, top, lines) => {
    if (branch.kind !== "group") {
        lines.push(top + laneMeasure.lineY);
        return;
    }
    let offset = top + laneMeasure.stackOffset;
    branch.lanes.forEach((member, index) => {
        const memberMeasure = laneMeasure.stack.lanes[index];
        collectEntryLines(member, memberMeasure, offset, lines);
        offset += memberMeasure.height + LANE_GAP;
    });
};

/** Linhas de entrada de todas as faixas de um bloco, na ordem em que aparecem. */
const entryLinesOf = (branches, elementMeasure) => {
    const lines = [];
    let top = 0;
    branches.forEach((branch, index) => {
        const laneMeasure = elementMeasure.lanes[index];
        collectEntryLines(branch, laneMeasure, top, lines);
        top += laneMeasure.height + LANE_GAP;
    });
    return lines;
};

/**
 * Mede um nível (sequência de nós numa mesma linha de fluxo): altura total e
 * posição da linha de fluxo dentro dele. Blocos de desvio centralizam a linha
 * entre a primeira e a última faixa — é o que produz o "Y".
 */
/**
 * Se o bloco precisa do contorno do fallback: o desvio devolve o fluxo ao
 * tronco quando nenhuma rota casa, e nenhum caminho converge para mostrar essa
 * continuidade. Havendo convergência, o merge já a desenha.
 */
const hasFallbackRail = (node) =>
    !!node.branches
    && node.branches.length > 0
    && switchContinuesOnNoMatch(node.item.action)
    && !node.branches.some((lane) => lane.path && !laneIsEnded(lane));

const measureElement = (node) => {
    if (!node.branches) {
        return { lineY: LINE, height: NODE_H };
    }
    const stack = measureStack(node.branches);
    const height = Math.max(stack.stackHeight, stack.lineY - LINE + NODE_H);
    // O contorno ganha uma faixa própria abaixo de tudo: nenhuma curva de
    // caminho passa ali, então não há como confundi-lo com uma saída deles.
    const rail = hasFallbackRail(node);
    return {
        lineY: stack.lineY,
        height: rail ? height + FALLBACK_RAIL_GAP : height,
        railY: rail ? height + FALLBACK_RAIL_GAP / 2 : null,
        lanes: stack.lanes,
        laneLines: stack.laneLines,
        stackHeight: stack.stackHeight,
    };
};

const measureLevel = (nodes) => {
    if (!nodes.length) {
        return { lineY: LINE, height: EMPTY_LANE_H, elements: [] };
    }
    const elements = nodes.map(measureElement);
    const lineY = Math.max(...elements.map((element) => element.lineY));
    const height = Math.max(
        ...elements.map((element) => lineY - element.lineY + element.height)
    );
    return { lineY, height, elements };
};

const shortText = (value, max = 26) => {
    const text = String(value ?? "").trim();
    if (!text) return "";
    return text.length > max ? `${text.slice(0, max)}…` : text;
};

const OPERATOR_SYMBOLS = {
    equals: "=",
    not_equals: "≠",
    greater_than: ">",
    less_than: "<",
    in: "em",
    not_in: "fora de",
    is_empty: "vazio",
    is_not_empty: "não vazio",
};

/**
 * Legenda do nó — o "placeholder" explicativo abaixo do nome, no estilo das
 * legendas do n8n ("getAll: row", "create: file"): resume a configuração do
 * passo, ou orienta o que falta configurar.
 */
export const getActionSubtitle = (action) => {
    const config = action.config || {};
    const loopPrefix = config.loopData && config.loopData.active ? "↻ " : "";

    switch (action.type) {
        case "condition": {
            if (!config.condition) return "defina a condição";
            const symbol = OPERATOR_SYMBOLS[config.condition] ?? config.condition;
            return `${loopPrefix}${shortText(config.referenceValue, 12)} ${symbol} ${shortText(config.comparisonValue, 12)}`;
        }
        case "switch": {
            // Conta todas as rotas (inclusive as ainda sem nome), para a
            // legenda bater com as faixas visíveis no canvas.
            const total = Array.isArray(config.routes) ? config.routes.length : 0;
            if (total === 0) return "defina as rotas";
            const mode = config.mode === "all" ? "todas as rotas" : "primeira rota";
            return `${mode} · ${total} rota${total > 1 ? "s" : ""}`;
        }
        case "merge": {
            const total = Array.isArray(config.sources) ? config.sources.length : 0;
            if (total < 2) return "escolha os caminhos a unir";
            return `une ${total} caminhos → ${shortText(config.label || config.name, 14)}`;
        }
        case "create_row":
            return config.fields?.form?.name
                ? `${loopPrefix}criar em: ${shortText(config.fields.form.name)}`
                : "escolha o formulário";
        case "update_row":
            return config.fields?.form?.name
                ? `${loopPrefix}atualizar: ${shortText(config.fields.form.name)}`
                : "escolha o formulário";
        case "delete_row":
            return config.formName?.name
                ? `${loopPrefix}excluir de: ${shortText(config.formName.name)}`
                : "escolha o formulário";
        case "send_mail":
            return config.sendTo
                ? `${loopPrefix}para: ${shortText(config.sendTo)}`
                : "defina o destinatário";
        case "script":
            return config.script ? `${loopPrefix}script js` : "escreva o script";
        case "delay":
            return config.delay ? `aguarda ${shortText(config.delay, 12)} ms` : "defina o atraso";
        case "query":
            return `${loopPrefix}consulta ao banco`;
        case "start_automation":
            return shortText(
                config.automation?.name ?? config.automantionName ?? ""
            ) || "escolha a automação";
        case "send_notificaiton":
            return config.title
                ? `${loopPrefix}${shortText(config.title)}`
                : "defina a notificação";
        case "end_flow":
            return "encerra a automação aqui";
        default:
            return null;
    }
};

/**
 * Canvas de fluxo estilo n8n: horizontal, nós em card com legenda, desvios em
 * garfo (Y) com o card centralizado entre os caminhos, merge das pontas em um
 * trilho único de continuação, pan por arraste e zoom. A inserção é
 * contextual: cada "+" sabe depois de qual passo e em qual rota insere.
 */
const FlowCanvas = ({
    actions,
    actionStep,
    trigger = null,
    hasPermissionToEdit = false,
    onSelect,
    onAdd,
}) => {
    const tree = buildFlowTree(actions ?? []);
    const rootMeasure = measureLevel(tree);
    // Etapas que o executor nunca alcança. Marcadas no desenho porque o estado
    // não é um evento: chega-se a ele adicionando um "Encerrar fluxo", movendo
    // um passo de caminho, removendo outro ou apagando uma rota do desvio.
    const unreachable = findUnreachableStepsFromTree(tree);
    const viewportRef = useRef(null);
    const dragState = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [zoom, setZoom] = useState(1);

    // Pan por arraste no fundo do canvas (nós e botões continuam clicáveis).
    const onMouseDown = (event) => {
        if (event.target.closest(".fc-node, .fc-node-name, button")) return;
        const viewport = viewportRef.current;
        if (!viewport) return;
        dragState.current = {
            x: event.clientX,
            y: event.clientY,
            scrollLeft: viewport.scrollLeft,
            scrollTop: viewport.scrollTop,
        };
        setDragging(true);

        const onMouseMove = (moveEvent) => {
            const start = dragState.current;
            if (!start) return;
            viewport.scrollLeft = start.scrollLeft - (moveEvent.clientX - start.x);
            viewport.scrollTop = start.scrollTop - (moveEvent.clientY - start.y);
        };
        const onMouseUp = () => {
            dragState.current = null;
            setDragging(false);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    };

    /** Conector horizontal com "+", com a linha do fluxo em `rowLine`.
     * Conectores do tronco (fora de qualquer caminho) são mais escuros, para
     * a linearidade se destacar das linhas de bifurcação. `dead` marca o trecho
     * que o executor nunca percorre: tracejado vermelho, do primeiro passo
     * inalcançável até o fim do ramo. */
    const renderConnector = (context, key, rowLine, isEnd = false, dead = false) => (
        <div
            className={`fc-hconn ${isEnd ? "fc-hconn-end" : ""} ${context.branch === null ? "fc-hconn-strong" : ""} ${dead ? "fc-hconn-dead" : ""}`}
            style={{ marginTop: rowLine - LINE }}
            key={key}
        >
            {hasPermissionToEdit ? (
                <Tooltip placement="top" title="Adicionar passo aqui">
                    <Button
                        className="fc-add"
                        size="small"
                        shape="circle"
                        icon={<PlusOutlined />}
                        onClick={() => onAdd(context)}
                    />
                </Tooltip>
            ) : null}
        </div>
    );

    const renderNodeCard = ({ action, index }, marginTop = 0) => {
        const name = getNameFromAction(action);
        const subtitle = getActionSubtitle(action);
        const terminalClass = action.type === "end_flow" ? "fc-node-terminal" : "";
        // Inalcançável: o passo continua clicável e configurável — só perde o
        // peso visual de quem executa, e a legenda diz o que está acontecendo.
        const isUnreachable = unreachable.has(index);
        return (
            <div
                className={`fc-nodebox ${terminalClass} ${isUnreachable ? "fc-node-unreachable" : ""} ${index === actionStep ? "fc-node-selected" : ""}`}
                style={marginTop ? { marginTop } : null}
            >
                <div className="fc-node" onClick={() => onSelect(index)}>
                    {action.icon}
                </div>
                <div className="fc-node-name" onClick={() => onSelect(index)}>
                    {name}
                </div>
                {isUnreachable
                    ? <div className="fc-node-sub fc-node-sub-warn">nunca executa</div>
                    : subtitle && <div className="fc-node-sub">{subtitle}</div>}
            </div>
        );
    };

    /** Canaleta de um lado do bloco de desvio (garfo à esquerda, merge à
     * direita), desenhada em SVG: uma curva por faixa, saindo/chegando na
     * linha do bloco. Substituiu o trilho vertical com cotovelos — a Bézier
     * mostra de relance a qual faixa cada fio pertence, porque o caminho é
     * contínuo em vez de compartilhar um trilho comum.
     * `strong` marca o bloco no fluxo linear: a entrada/saída do tronco ganha
     * o mesmo peso dos conectores lineares. */
    const renderGutter = (side, boxHeight, lines, blockLine, strong) => {
        // Sem linha para desenhar não há canaleta. Antes ela caía num traço
        // reto de 56px mesmo vazia: uma linha que nascia no vazio (nada a
        // alimentava) e ainda afastava a continuação do tronco na mesma medida.
        // É o que se via quando todos os caminhos do desvio terminavam em
        // "Encerrar fluxo" e ele encerrava a automação no "nenhum caminho
        // casar" — a etapa seguinte aparecia solta, no fim de um fio sem
        // origem. Sem canaleta ela encosta no bloco pelo conector comum, com o
        // mesmo espaçamento de qualquer par de etapas.
        //
        // O caso oposto entra aqui como linha de verdade: quando o desvio
        // continua no fluxo principal, a saída existe (é o fallback) e quem a
        // chama passa a reta que a representa.
        if (!lines.length) return null;

        const height = Math.max(boxHeight, blockLine + LINE);
        const isEntry = side === "entry";
        // `strong` só vale quando alguma curva viva sai por aqui: um trecho de
        // tronco cheio sobre um leque todo morto contradiria o resto.
        const anyAlive = lines.some((line) => !line.dead);

        const edges = lines.map((line, laneIndex) => (
            <path
                key={`edge-${laneIndex}`}
                className={`fc-edge ${line.fallback ? "fc-edge-fallback" : ""} ${line.dead ? "fc-edge-dead" : ""}`}
                d={isEntry
                    ? edgePath(0, blockLine, GUTTER_W, line.y)
                    : edgePath(0, line.y, GUTTER_W, blockLine)}
            />
        ));

        return (
            <svg
                className="fc-gutter"
                width={GUTTER_W}
                height={height}
                style={{ height }}
                aria-hidden="true"
            >
                {edges}
                {/* Trecho do tronco: sobreposto às curvas para a continuidade
                    do fluxo linear não se perder no leque. */}
                {strong && anyAlive ? (
                    <path
                        className="fc-edge fc-edge-strong"
                        d={isEntry
                            ? `M 0 ${blockLine} L 14 ${blockLine}`
                            : `M ${GUTTER_W - 14} ${blockLine} L ${GUTTER_W} ${blockLine}`}
                    />
                ) : null}
            </svg>
        );
    };

    /** Saída da faixa morta: ou o último passo dela não executa, ou o caminho
     * se fecha antes de entregar alguma coisa ao merge (um desvio aninhado com
     * todas as rotas encerradas, por exemplo). */
    const laneExitIsDead = (branch, enteringDead) => {
        if (enteringDead) return true;
        const last = branch.children[branch.children.length - 1];
        if (last && unreachable.has(last.item.index)) return true;
        return laneClosesFlow(branch);
    };

    /**
     * Uma faixa: o rótulo do caminho, a ligação até o primeiro passo e os
     * passos dela. `fallbackIndex` é onde entra um passo novo quando a faixa
     * ainda está vazia.
     */
    const renderLane = (branch, laneMeasure, laneKey, fallbackIndex, enteringDead = false) => {
        if (branch.kind === "group") {
            return renderGroup(branch, laneMeasure, laneKey, fallbackIndex, enteringDead);
        }

        // Caminho ainda sem nome: só o chip pendente, pendurado no garfo, sem
        // conectores.
        if (!branch.path) {
            return (
                <div
                    className="fc-lane"
                    style={{ height: laneMeasure.height }}
                    key={laneKey}
                >
                    <div
                        className="fc-lane-label fc-lane-label-pending"
                        style={{ marginTop: laneMeasure.lineY - 12 }}
                    >
                        {branch.label}
                    </div>
                </div>
            );
        }

        const ended = laneIsEnded(branch);
        // A ligação até o primeiro passo morre com a entrada da faixa; a linha
        // elástica até o merge, com a saída dela — no meio, cada conector
        // acompanha o passo a que leva (ver `renderLevel`).
        const exitDead = laneExitIsDead(branch, enteringDead);
        return (
            <div
                className="fc-lane"
                style={{ height: laneMeasure.height }}
                key={laneKey}
            >
                <div
                    className={`fc-lane-label ${enteringDead ? "fc-lane-label-dead" : ""}`}
                    style={{ marginTop: laneMeasure.lineY - 12 }}
                >
                    {branch.label}
                </div>
                <div
                    className={`fc-lane-link ${enteringDead ? "fc-line-dead" : ""}`}
                    style={{ marginTop: laneMeasure.lineY - 1 }}
                />
                {renderLevel(
                    branch.children,
                    laneMeasure,
                    laneMeasure.lineY,
                    branch.path,
                    true,
                    enteringDead
                )}
                {/* Caminho encerrado (end_flow) não oferece "+" nem se conecta
                    ao merge — termina ali. */}
                {!ended && renderConnector(
                    {
                        afterIndex: branch.lastIndex ?? fallbackIndex,
                        branch: branch.path,
                    },
                    `add-${laneKey}`,
                    laneMeasure.lineY,
                    true,
                    exitDead
                )}
                {!ended && (
                    <div
                        className={`fc-lane-fill ${exitDead ? "fc-line-dead" : ""}`}
                        style={{ marginTop: laneMeasure.lineY - 1 }}
                    />
                )}
            </div>
        );
    };

    /**
     * Grupo de junção dentro de um bloco de desvio: as faixas unidas terminam
     * nas curvas que entram no card da junção, e a continuação comum sai dele.
     * As faixas que a junção não une continuam inteiras, ao lado — é o que
     * mostra de relance quais caminhos voltaram a ser um só e quais seguem
     * separados até a convergência final.
     */
    const renderGroup = (group, groupMeasure, groupKey, fallbackIndex, enteringDead = false) => {
        const { stack, tail, lineY } = groupMeasure;
        const connectedLines = groupMeasure.laneLines
            .map((y, laneIndex) => ({
                y,
                lane: group.lanes[laneIndex],
                dead: laneExitIsDead(group.lanes[laneIndex], enteringDead),
            }))
            .filter(({ lane }) => lane.path && !laneIsEnded(lane));
        const ended = laneIsEnded(group);
        // A junção não executa quando nenhuma origem chega: daí para a direita
        // (card, continuação e saída) o ramo inteiro é morto.
        const mergeDead = enteringDead || unreachable.has(group.item.index);

        return (
            <div
                className="fc-lane"
                style={{ height: groupMeasure.height }}
                key={groupKey}
            >
                <div
                    className="fc-lanes"
                    style={{ marginTop: groupMeasure.stackOffset }}
                >
                    {group.lanes.map((lane, laneIndex) =>
                        renderLane(
                            lane,
                            stack.lanes[laneIndex],
                            `${groupKey}-${laneIndex}`,
                            fallbackIndex,
                            enteringDead
                        )
                    )}
                </div>
                {renderGutter("merge", groupMeasure.height, connectedLines, lineY, false)}
                {renderNodeCard(group.item, lineY - LINE)}
                {/* Junção ainda sem nome não tem caminho para pendurar passos:
                    o card aparece recebendo as faixas, e o "+" só surge quando
                    o caminho unificado tiver nome. */}
                {group.path && group.children.length > 0 && renderConnector(
                    { afterIndex: group.item.index, branch: group.path },
                    `after-${group.item.index}`,
                    lineY,
                    false,
                    mergeDead || unreachable.has(group.children[0].item.index)
                )}
                {renderLevel(group.children, tail, lineY, group.path, true, mergeDead)}
                {group.path && !ended && renderConnector(
                    { afterIndex: group.lastIndex, branch: group.path },
                    `add-${groupKey}`,
                    lineY,
                    true,
                    laneExitIsDead(group, mergeDead)
                )}
                {group.path && !ended && (
                    <div
                        className={`fc-lane-fill ${laneExitIsDead(group, mergeDead) ? "fc-line-dead" : ""}`}
                        style={{ marginTop: lineY - 1 }}
                    />
                )}
            </div>
        );
    };

    /**
     * Renderiza um nível na linha de fluxo `rowLine`. `suppressTrailing` omite
     * o conector do último nó dentro de faixas (o "+" da faixa cobre).
     *
     * `enteringDead` é o fluxo que chega ao nível já morto (faixa de um desvio
     * inalcançável, continuação de junção que não recebe origem). Dentro do
     * nível, cada conector morre com o passo a que leva — assim o tracejado
     * vermelho começa exatamente no primeiro passo que não executa e segue até
     * o fim do ramo.
     */
    const renderLevel = (nodes, measure, rowLine, lanePath, suppressTrailing = false, enteringDead = false) =>
        nodes.map((node, nodeIndex) => {
            const elementMeasure = measure.elements[nodeIndex];
            const isLast = nodeIndex === nodes.length - 1;
            const nextNode = nodes[nodeIndex + 1];
            // O conector sai deste passo e chega no próximo: morre se qualquer
            // uma das duas pontas não executa. No último do nível não há
            // próximo — vale o próprio passo (o "+" pendurado no fim do ramo).
            const trailingDead = enteringDead
                || unreachable.has(node.item.index)
                || (!!nextNode && unreachable.has(nextNode.item.index));

            // Desvio recém-adicionado (nenhuma rota declarada) não abre faixa
            // nenhuma: desenhá-lo como bloco deixava o card entre duas
            // canaletas vazias — 112px de linha reta até a etapa seguinte. Sem
            // faixas ele é um card comum, e a continuação segue colada.
            if (!node.branches || node.branches.length === 0) {
                return (
                    <React.Fragment key={node.item.index}>
                        {renderNodeCard(node.item, rowLine - LINE)}
                        {node.item.action.type === "end_flow"
                            ? null
                            : (!suppressTrailing || !isLast) && renderConnector(
                                { afterIndex: node.item.index, branch: lanePath ?? null },
                                `after-${node.item.index}`,
                                rowLine,
                                isLast,
                                trailingDead
                            )}
                    </React.Fragment>
                );
            }

            const blockLine = elementMeasure.lineY;
            // O bloco inteiro (garfo, faixas e merge) morre junto com o desvio.
            const blockDead = enteringDead || unreachable.has(node.item.index);
            // Garfo: um fio por caminho declarado, inclusive os que uma junção
            // recolheu para dentro de um grupo. Todos os caminhos de um desvio
            // alcançado são alcançados — o garfo só morre com o desvio.
            // Contorno do fallback: sai do desvio junto com o garfo, corre por
            // baixo de todas as faixas e volta ao tronco na saída do bloco.
            // É o desenho do "nenhum caminho casou": não toca caminho nenhum.
            const railY = elementMeasure.railY;
            const railLine = railY === null || railY === undefined
                ? null
                : { y: railY, dead: blockDead, fallback: true };

            const entryLines = entryLinesOf(node.branches, elementMeasure)
                .map((y) => ({ y, dead: blockDead }))
                .concat(railLine ? [railLine] : []);
            // Merge: só o que sai do bloco — a saída de um grupo é a da junção,
            // não a de cada caminho que ela uniu. Caminho sem nome ou encerrado
            // não se conecta.
            const connectedLines = elementMeasure.laneLines
                .map((y, laneIndex) => ({
                    y,
                    lane: node.branches[laneIndex],
                    dead: laneExitIsDead(node.branches[laneIndex], blockDead),
                }))
                .filter(({ lane }) => lane.path && !laneIsEnded(lane));
            // Com todos os caminhos encerrados, é o contorno — e só ele — que
            // alimenta a continuação. Sem desenhá-lo o tronco fica visualmente
            // cortado e o conector seguinte volta a nascer do vazio.
            const exitLines = connectedLines.length
                ? connectedLines
                : (railLine ? [railLine] : []);

            return (
                <React.Fragment key={node.item.index}>
                    <div
                        className="fc-block"
                        style={{ marginTop: rowLine - blockLine }}
                    >
                        {renderNodeCard(node.item, blockLine - LINE)}
                        {renderGutter("entry", elementMeasure.height, entryLines, blockLine, lanePath === null)}
                        <div className="fc-lanes">
                            {/* Trecho reto do contorno, ligando as duas curvas
                                por baixo das faixas. Absoluto e esticado de
                                borda a borda: acompanha a largura da coluna,
                                que depende do caminho mais longo. */}
                            {railLine && (
                                <div
                                    className={`fc-fallback-rail ${blockDead ? "fc-line-dead" : ""}`}
                                    // Metade da espessura: o centro do trilho
                                    // cai exatamente em `railY`, onde as duas
                                    // curvas das canaletas terminam.
                                    style={{ top: railY - 1.25 }}
                                />
                            )}
                            {node.branches.map((branch, laneIndex) =>
                                // Key POSICIONAL: nomes duplicados ou vazios no
                                // editor não podem corromper a reconciliação da
                                // lista (faixa fantasma/desconectada).
                                renderLane(
                                    branch,
                                    // Reaproveita a medição feita em measureElement.
                                    elementMeasure.lanes[laneIndex],
                                    `lane-${node.item.index}-${laneIndex}`,
                                    node.laneLastIndex ?? node.blockLastIndex,
                                    blockDead
                                )
                            )}
                        </div>
                        {renderGutter("merge", elementMeasure.height, exitLines, blockLine, lanePath === null)}
                    </div>
                    {(!suppressTrailing || !isLast) && renderConnector(
                        // Merge: o próximo passo continua o fluxo linear,
                        // independentemente do caminho executado.
                        { afterIndex: node.blockLastIndex, branch: lanePath ?? null },
                        `join-${node.item.index}`,
                        rowLine,
                        isLast,
                        trailingDead
                    )}
                </React.Fragment>
            );
        });

    const rootLine = rootMeasure.lineY;
    const triggerSubtitle = trigger && trigger.type
        ? getNameFromTriggerByType(trigger.type)
        : "defina o gatilho";

    return (
        <div
            ref={viewportRef}
            className={`fc-viewport ${dragging ? "fc-dragging" : ""}`}
            onMouseDown={onMouseDown}
        >
            <div className="fc-zoom-controls">
                <Tooltip placement="left" title="Afastar">
                    <Button size="small" type="text" icon={<MinusOutlined />}
                        onClick={() => setZoom((current) => Math.max(0.5, +(current - 0.1).toFixed(2)))} />
                </Tooltip>
                <Tooltip placement="left" title="Restaurar zoom">
                    <Button size="small" type="text" className="fc-zoom-level" onClick={() => setZoom(1)}>
                        {Math.round(zoom * 100)}%
                    </Button>
                </Tooltip>
                <Tooltip placement="left" title="Aproximar">
                    <Button size="small" type="text" icon={<PlusOutlined />}
                        onClick={() => setZoom((current) => Math.min(1.5, +(current + 0.1).toFixed(2)))} />
                </Tooltip>
            </div>
            <div className="fc-canvas" style={{ transform: `scale(${zoom})` }}>
                <div className="fc-row">
                    <div
                        className={`fc-nodebox fc-node-trigger ${actionStep === -1 ? "fc-node-selected" : ""}`}
                        style={rootLine !== LINE ? { marginTop: rootLine - LINE } : null}
                    >
                        <div className="fc-node" onClick={() => onSelect(-1)}>
                            <BsFillPlayCircleFill />
                        </div>
                        <div className="fc-node-name" onClick={() => onSelect(-1)}>
                            Gatilho
                        </div>
                        <div className="fc-node-sub">{triggerSubtitle}</div>
                    </div>
                    {renderConnector({ afterIndex: -1, branch: null }, "after-trigger", rootLine, tree.length === 0)}
                    {renderLevel(tree, rootMeasure, rootLine, null)}
                </div>
            </div>
        </div>
    );
};

export default FlowCanvas;
